import ShareIcon from '@mui/icons-material/Share.js';
/* eslint-disable import/extensions */
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { queryString } from '@woovi/shared';
import { BoxFlex, ActionButton } from '@woovi/ui';
import { convertToMoney, maskMoney } from '@woovi/utils';

import type { PaymentLinkShareButton_charge$key } from './__generated__/PaymentLinkShareButton_charge.graphql.ts';
import type { PaymentLinkShareButton_pixQrCode$key } from './__generated__/PaymentLinkShareButton_pixQrCode.graphql.ts';

const { stringify } = queryString;

type PaymentPixAutomaticLinkShareButtonProps = {
  pixAutomatic: PaymentLinkShareButton_charge$key;
};

const PaymentPixAutomaticLinkShareButton = (
  props: PaymentPixAutomaticLinkShareButtonProps,
): ReactNode => {
  const { t } = useTranslation();

  const pixAutomatic = useFragment<PaymentLinkShareButton_pixQrCode$key>(
    graphql`
      fragment PaymentPixAutomaticLinkShareButton_subscription on PaymentSubscription {
        company {
          nameFriendly
          projectType
        }
        value
        pixRecurring {
          paymentLinkUrl
        }
        customer {
          phone
        }
      }
    `,
    props.pixAutomatic,
  );

  const url = pixAutomatic.pixRecurring?.paymentLinkUrl;

  const value = maskMoney(pixAutomatic.value / 100);

  const title = t('Pix Automatic', {
    money: value,
    merchantName: pixAutomatic.company.nameFriendly,
  });

  const text = t(
    'Approve the Pix Automatic of {{value}} to {{merchant}}. {{link}}',
    {
      value: convertToMoney(pixAutomatic.value),
      merchant: pixAutomatic.company?.nameFriendly,
      link: url || '',
    },
  );

  const shareOpen = (): void => {
    const isDesktop = !navigator.userAgent.match(/Android|iPhone|iPad|iPod/i);

    const phone = pixAutomatic?.customer?.phone;

    const queryString = stringify({ text, phone });

    window.open(
      (isDesktop ? 'https://api.whatsapp.com/send?' : 'whatsapp://send?') +
        queryString,
    );
  };

  const share = async (): void => {
    const isWindows = !!navigator.userAgent.match(/Windows/i);
    const isMac = !!navigator.userAgent.match(/Macintosh/i);
    const isSafari = !!navigator.userAgent.match(/Safari/i);

    if (isWindows || (isMac && isSafari)) {
      shareOpen();

      return;
    }

    if (!!navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url,
        });
      } catch (err) {
        // eslint-disable-next-line
        console.log('share err', err);

        shareOpen();
      }
    } else {
      shareOpen();
    }
  };

  const sharePixAutomatic = t('Share pix automatic');

  return (
    <BoxFlex
      sx={{
        flex: 1,
      }}
    >
      <ActionButton
        sx={(theme) => ({
          width: '100%',
          color: theme.palette.tertiary.main,
          '&:hover': {
            color: theme.palette.tertiary.main,
          },
          '&:focus': {
            color: theme.palette.tertiary.main,
            backgroundColor: theme.palette.common.white,
          },
        })}
        variant='outlined'
        color={'tertiary'}
        startIcon={<ShareIcon color={'tertiary'} />}
        onClick={share}
        size={'large'}
      >
        <Typography
          as='span'
          sx={(theme) => ({
            textTransform: 'uppercase',
            color: theme.palette.tertiary.main,
          })}
        >
          {sharePixAutomatic}
        </Typography>
      </ActionButton>
    </BoxFlex>
  );
};

export default PaymentPixAutomaticLinkShareButton;
